import React, { useState, useEffect } from 'react';
import { memberService } from '../../services/api';
import { FiSettings, FiBell, FiMail, FiEye, FiSave, FiCheck } from 'react-icons/fi';
import './Settings.css';

const Settings = () => {
  const [settings, setSettings] = useState({
    notifyOnPost: true,
    notifyOnComment: true,
    notifyOnLike: true,
    notifyOnMessage: true,
    notifyOnEvent: true,
    notifyOnBirthday: true,
    emailDigestFrequency: 'never',
    digestPosts: true,
    digestPhotos: true,
    digestEvents: true,
    digestBirthdays: true,
    digestComments: true,
    digestReactions: true,
    digestMessages: true,
    profileVisibility: 'family',
    showBirthYear: true,
    timeZone: 'UTC',
    language: 'en'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await memberService.getSettings();
      if (response.success && response.data) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (field) => {
    setSettings(prev => ({ ...prev, [field]: !prev[field] }));
    setSavedMessage('');
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSavedMessage('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSavedMessage('');
      const response = await memberService.updateSettings(settings);
      if (response.success) {
        setSavedMessage('Settings saved successfully!');
        setTimeout(() => setSavedMessage(''), 3000);
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      setSavedMessage('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="container">
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="container">
        <div className="page-header">
          <h1><FiSettings className="page-header-icon" /> Settings</h1>
          <p className="page-subtitle">Manage your preferences and notifications</p>
        </div>

        {savedMessage && (
          <div className={`settings-message ${savedMessage.includes('Failed') ? 'error' : 'success'}`}>
            <FiCheck /> {savedMessage}
          </div>
        )}

        {/* Notifications */}
        <div className="settings-section card">
          <div className="card-body">
            <h2 className="settings-section-title">
              <FiBell /> Notifications
            </h2>
            <p className="settings-section-description">
              Choose what you want to be notified about.
            </p>

            <div className="settings-options">
              <ToggleOption
                label="New posts"
                description="When someone shares a new post"
                checked={settings.notifyOnPost}
                onChange={() => handleToggle('notifyOnPost')}
              />
              <ToggleOption
                label="Comments"
                description="When someone comments on your posts"
                checked={settings.notifyOnComment}
                onChange={() => handleToggle('notifyOnComment')}
              />
              <ToggleOption
                label="Reactions"
                description="When someone reacts to your posts"
                checked={settings.notifyOnLike}
                onChange={() => handleToggle('notifyOnLike')}
              />
              <ToggleOption
                label="Messages"
                description="When you receive a direct message"
                checked={settings.notifyOnMessage}
                onChange={() => handleToggle('notifyOnMessage')}
              />
              <ToggleOption
                label="Events"
                description="When new events are created or updated"
                checked={settings.notifyOnEvent}
                onChange={() => handleToggle('notifyOnEvent')}
              />
              <ToggleOption
                label="Birthdays"
                description="Reminders for family birthdays"
                checked={settings.notifyOnBirthday}
                onChange={() => handleToggle('notifyOnBirthday')}
              />
            </div>
          </div>
        </div>

        {/* Email Digest */}
        <div className="settings-section card">
          <div className="card-body">
            <h2 className="settings-section-title">
              <FiMail /> Email Digest
            </h2>
            <p className="settings-section-description">
              Get a summary of family activity sent to your email.
            </p>

            <div className="settings-options">
              <div className="setting-row">
                <label className="form-label">Frequency</label>
                <select
                  className="form-input"
                  value={settings.emailDigestFrequency}
                  onChange={(e) => handleChange('emailDigestFrequency', e.target.value)}
                >
                  <option value="never">Never</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              {settings.emailDigestFrequency !== 'never' && (
                <>
                  <ToggleOption
                    label="Photos"
                    description="Include new photos in your digest"
                    checked={settings.digestPhotos}
                    onChange={() => handleToggle('digestPhotos')}
                  />
                  <ToggleOption
                    label="Posts"
                    description="Include new posts in your digest"
                    checked={settings.digestPosts}
                    onChange={() => handleToggle('digestPosts')}
                  />
                  <ToggleOption
                    label="Comments"
                    description="Include comments on your posts in your digest"
                    checked={settings.digestComments}
                    onChange={() => handleToggle('digestComments')}
                  />
                  <ToggleOption
                    label="Reactions"
                    description="Include reactions to your posts in your digest"
                    checked={settings.digestReactions}
                    onChange={() => handleToggle('digestReactions')}
                  />
                  <ToggleOption
                    label="Messages"
                    description="Include unread messages in your digest (coming soon)"
                    checked={settings.digestMessages}
                    onChange={() => handleToggle('digestMessages')}
                  />
                  <ToggleOption
                    label="Events"
                    description="Include upcoming events in your digest"
                    checked={settings.digestEvents}
                    onChange={() => handleToggle('digestEvents')}
                  />
                  <ToggleOption
                    label="Birthdays"
                    description="Include family birthdays in your digest"
                    checked={settings.digestBirthdays}
                    onChange={() => handleToggle('digestBirthdays')}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Privacy */}
        {/* <div className="settings-section card">
          <div className="card-body">
            <h2 className="settings-section-title">
              <FiEye /> Privacy
            </h2>
            <p className="settings-section-description">
              Control what others can see about you.
            </p>

            <div className="settings-options">
              <ToggleOption
                label="Show birth year"
                description="Display your birth year on your profile (not just month and day)"
                checked={settings.showBirthYear}
                onChange={() => handleToggle('showBirthYear')}
              />
            </div>
          </div>
        </div> */}

        {/* Save Button */}
        <div className="settings-save">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <><span className="spinner spinner-sm"></span> Saving...</>
            ) : (
              <><FiSave /> Save Settings</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleOption = ({ label, description, checked, onChange }) => (
  <div className="setting-row">
    <div className="setting-info">
      <div className="setting-label">{label}</div>
      <div className="setting-description">{description}</div>
    </div>
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="toggle-slider"></span>
    </label>
  </div>
);

export default Settings;